import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, CheckCircle2, ShipWheel } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { apiClient } from '../api/client';
import fluxcdIcon from '../assets/icons/flux-cd.svg';

export default function FluxCDIntegration() {
  const [clusters, setClusters] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await apiClient.getFluxClusters();
        setClusters(result);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const connected = clusters.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 flex items-center justify-center rounded-lg bg-muted">
                <img src={fluxcdIcon} alt="Flux CD" className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Flux CD</h1>
                <p className="text-sm text-muted-foreground">GitOps automation: reconcile, suspend, and resume Kustomizations, HelmReleases, and sources from workflows</p>
              </div>
            </div>
            {connected ? (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Detected
              </Badge>
            ) : (
              <Badge variant="secondary">Not Detected</Badge>
            )}
          </div>

          {connected ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Flux CD was detected on the following clusters. Flux actions are available in the workflow builder.
              </p>
              <div className="space-y-2">
                {clusters.map(c => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                    <ShipWheel className="w-4 h-4 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">Flux CD CRDs detected</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Flux CD is detected automatically by the Kestrel operator. Install Flux in a cluster
                where the operator is running and the integration will activate automatically
                — no credentials required.
              </p>
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-sm font-medium mb-1">Requirements</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                  <li>Kestrel operator installed in the cluster</li>
                  <li>Flux controllers and CRDs installed (kustomize.toolkit.fluxcd.io, helm.toolkit.fluxcd.io, source.toolkit.fluxcd.io)</li>
                </ul>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/onboard-cluster">Onboard a cluster</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
